import isEmpty from 'lodash/isEmpty';
import union from 'lodash/union';
import keys from 'lodash/keys';
import reduce from 'lodash/reduce';
import firebaseApp from '../../../firebase-app';
import { PermissionsTeam, Team, TeamMinimized, TeamsFromDB } from './types';
import svStrings from '../../../sv-strings';
import { ROLES, TeamPermissions, TeamRoles, SuperAdminUser } from '../../../types';
import { collection, doc, getDoc } from 'firebase/firestore';
import { AuthContextValues } from '../../../contexts/auth';

export const getTeamPermissionsToastMessage = (data: Record<string, string>): string => {
  if (!isEmpty(data)) {
    const updatedTeams = Object.keys(data).join(', ');
    return svStrings.usersAndPermissions.teamsModifiedSuccessfully.replace(
      '$updatedTeams',
      updatedTeams
    );
  } else {
    return svStrings.messages.error;
  }
};

export const fetchBackendUserTeam = async (uid: string) => {
  const colRef = collection(firebaseApp.db, '/backend/auth/users');
  const docSnap = await getDoc(doc(colRef, uid));
  return docSnap.data()?.teams;
};

export const removeUneditedChanges = (
  initialTeamPermissions: TeamPermissions,
  newTeamPermissions: TeamPermissions
) => {
  const initialTeamAbbrvs = Object.keys(initialTeamPermissions);
  const newTeamAbbrvs = Object.keys(newTeamPermissions);
  const modifiedPermissions = {} as TeamPermissions;

  // Add new permissions
  newTeamAbbrvs.forEach((teamAbbrv) => {
    if (initialTeamPermissions[teamAbbrv] !== newTeamPermissions[teamAbbrv]) {
      modifiedPermissions[teamAbbrv] = newTeamPermissions[teamAbbrv];
    }
  });

  // Add removed permissions
  initialTeamAbbrvs.forEach((teamAbbrv) => {
    if (!newTeamAbbrvs.includes(teamAbbrv)) {
      modifiedPermissions[teamAbbrv] = ROLES.NONE;
    }
  });

  return modifiedPermissions;
};

export const getAllTeamsWithPermission = (
  allTeamsFromDB: TeamsFromDB[],
  userTeams: TeamPermissions,
  isUserSuperAdmin: boolean
) => {
  const teamsWithUserPermissionRoles: Team[] = allTeamsFromDB.reduce((acc: any, team) => {
    const result = [...acc];

    if (isUserSuperAdmin) {
      const role = userTeams[team.abbrv] || '';
      const everyTeam = { ...team, role };

      result.push(everyTeam);

      return result;
    } else if (team.abbrv in userTeams) {
      const filteredTeam = {
        ...team,
        role: userTeams[team.abbrv],
      };

      result.push(filteredTeam);

      return result;
    }

    return result;
  }, []);

  return teamsWithUserPermissionRoles;
};

export const removeTeamsWithoutPermissionToEdit = (
  teams: Team[],
  adminSuperAdminUser: SuperAdminUser,
  clickedCellUserID: string
) => {
  const adminUserTeams = adminSuperAdminUser.teams;

  if (!adminUserTeams) {
    return [];
  }

  return teams.map((team) => {
    if (adminSuperAdminUser.uid === clickedCellUserID) {
      return team;
    }

    if (adminUserTeams[team.abbrv] === ROLES.ADMIN) {
      return { ...team, hasPermission: true };
    }
    return team;
  });
};

export const getTeamPermissionDiff = (adminTeams: TeamPermissions, userTeams: TeamPermissions) => {
  const allkeys = union(keys(adminTeams), keys(userTeams));
  const difference = reduce(
    allkeys,
    function (result: any, key) {
      if (adminTeams[key] === ROLES.ADMIN && typeof userTeams?.[key] !== 'undefined') {
        result[key] = userTeams[key];
        return result;
      }
      return result;
    },
    {}
  );

  return difference;
};

export const removeInaccessibleUsersAndTeams = (
  auth: AuthContextValues,
  users: SuperAdminUser[]
) => {
  const userTeams = users
    .map((user) => {
      let teams = {};

      try {
        teams = getTeamPermissionDiff(auth.teamPermissions, user.teams);
      } catch (error) {
        console.error('Error getting team permissions', error, user, user.teams);
      }

      if (auth.isSuperAdmin || user.uid === auth.currentUser?.uid) {
        return user;
      }

      if (isEmpty(teams) && !isEmpty(user.teams)) {
        return {} as SuperAdminUser;
      }

      return {
        ...user,
        teams,
      };
    })
    .filter((user) => !isEmpty(user));

  return userTeams;
};

export const convertTeamsPermissions = (teamPermissions: TeamPermissions) => {
  return Object.entries(teamPermissions).reduce((permissionsTeam, [abbrv, role]) => {
    return {
      ...permissionsTeam,
      [role]: [...(permissionsTeam[role] || []), abbrv],
    };
  }, {} as PermissionsTeam);
};

export const convertPermissionsTeam = (permissionsTeam: PermissionsTeam) => {
  return Object.entries(permissionsTeam).reduce((teamPermissions, [role, abbrvs]) => {
    abbrvs.forEach((abbrv) => {
      teamPermissions[abbrv] = role as TeamRoles;
    });
    return teamPermissions;
  }, {} as TeamPermissions);
};

export const getTeamName = (appTeams: TeamMinimized[], teamAbbrv: string) => {
  const foundAppTeam = appTeams.find(({ abbrv }) => abbrv === teamAbbrv);
  return foundAppTeam ? foundAppTeam.name : teamAbbrv;
};
